// Investor workflow service — pilot shells only. All sensitive actions
// must traverse geo-api so JWT, audit trail, and NDA gating live server-side.
import { geoApi } from '@/lib/geoApi';

/** Investor status taxonomy (matches geo-api enum). */
export type InvestorStatus =
  | 'public_visitor'
  | 'registered_investor'
  | 'verified_investor'
  | 'shortlisted_investor'
  | 'approved_investor'
  | 'strategic_partner'
  | 'restricted_access_investor'
  | 'suspended_investor'
  // legacy values kept for backwards compat with existing UI
  | 'guest'
  | 'pending'
  | 'verified'
  | 'restricted';

export interface InvestorProfileSummary {
  status: InvestorStatus;
  ndaSigned: boolean;
  mouSigned: boolean;
  loiSubmitted: boolean;
  kycComplete: boolean;
  kycCompletenessScore?: number;
  organization?: string;
  fullName?: string;
  /** VUI1-FIX1: NDA/MOU/LOI could not be read (both stage sources failed) — the
   *  boolean flags above are then NOT authoritative and must not be displayed. */
  stagesUnavailable?: boolean;
  /** VUI1-FIX1: the KYC read failed — kycComplete / kycCompletenessScore are not authoritative. */
  kycUnavailable?: boolean;
}

/** VUI1-FIX1: a failed authoritative read. Carries the HTTP status (0 = transport) so callers
 *  can tell "unavailable" from an authoritative answer; never converted into a default value. */
export class InvestorReadError extends Error {
  readonly status: number;
  constructor(message: string, status: number) { super(message); this.name = 'InvestorReadError'; this.status = status; }
}
const readFailure = (res: { error?: unknown; status?: number }, what: string): InvestorReadError =>
  new InvestorReadError(typeof res.error === 'string' && res.error ? res.error : `${what} could not be loaded.`, typeof res.status === 'number' ? res.status : 0);

export interface ReviewTimelineEntry {
  id: string;
  step: string;
  state: 'pending' | 'in-progress' | 'approved' | 'rejected';
  actor?: string;
  at?: string;
  note?: string;
}

export interface DocumentAccessEntry {
  id: string;
  documentTitle: string;
  action: 'viewed' | 'downloaded' | 'requested' | 'denied';
  classification?: string;
  at: string;
}



/* ───────────────── Enhanced Investor KYC model ───────────────── */

/** KYC lifecycle status taxonomy (mirrors geo-api enum). */
export type KycStatus =
  | 'not_started'
  | 'pending'
  | 'incomplete'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'rejected';

/** Sequential workflow stages — official GIISDEC order. */
export type WorkflowStageKey = 'loi' | 'nda' | 'payment' | 'mou';

export type WorkflowStageStatus =
  | 'locked'
  | 'available'
  | 'in_progress'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'rejected';

export interface WorkflowStage {
  key: WorkflowStageKey;
  status: WorkflowStageStatus;
  updatedAt?: string;
  note?: string;
}

export interface DocumentLifecycleEntry {
  id: string;
  stage: WorkflowStageKey;
  title: string;
  uploadStatus: 'missing' | 'uploaded' | 'verified' | 'rejected';
  signatureStatus?: 'not_required' | 'pending' | 'signed';
  at?: string;
}

/** Enhanced investor KYC record (read model). All fields optional so the UI
 * always renders a safe empty state when geo-api returns partial data. */
export interface InvestorKycProfile {
  kycStatus: KycStatus;
  completenessScore: number; // 0..100
  email?: string;
  phone?: string;
  organizationName?: string;
  organizationRegistration?: string;
  organizationAddress?: string;
  countryOfOperation?: string;
  businessCategory?: string;
  representativeName?: string;
  representativeRole?: string;
  representativeEmail?: string;
  idType?: string;
  idNumber?: string;
  taxId?: string;
  investorClassification?: string;
  projectInterests?: string[];
  mineralInterests?: string[];
  documentsUploaded?: number;
  updatedAt?: string;
}

/** Full enhanced KYC submission payload. */
export interface EnhancedKycPayload {
  email: string;
  phone: string;
  organizationName: string;
  organizationRegistration?: string;
  organizationAddress?: string;
  countryOfOperation: string;
  businessCategory?: string;
  representativeName: string;
  representativeRole?: string;
  representativeEmail?: string;
  idType: string;
  idNumber: string;
  taxId?: string;
  investorClassification?: string;
  projectInterests?: string[];
  mineralInterests?: string[];
  declarationAccepted: boolean;
}

/** Legacy/basic KYC payload (kept for backwards compatibility). */
export interface KycPayload {
  fullName: string;
  organization: string;
  country: string;
  idType: string;
  idNumber: string;
  declarationAccepted: boolean;
}


export interface DocumentRequestPayload {
  projectId?: string;
  documentType: string;
  reason: string;
}

export interface PermitApplicationPayload {
  permitType: string;
  projectId?: string;
  region: string;
  notes?: string;
}

export interface RestrictedDataPayload {
  datasetId: string;
  justification: string;
  ndaAcknowledged: boolean;
}

export interface AgreementRequestPayload {
  type: 'NDA' | 'MOU' | 'LOI';
  counterparty: string;
  scope: string;
}

export interface SupportTicketPayload {
  subject: string;
  category: string;
  message: string;
}

export interface ApplicationDocumentRecord {
  id: string;
  application_id?: string;
  document_type?: string;
  file_name?: string;
  mime_type?: string | null;
  storage_path?: string;
  checksum?: string | null;
  version?: number;
  status?: string;
  uploaded_by?: string | null;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface ApplicationReviewRecord {
  id: string;
  application_id?: string;
  reviewer_id?: string;
  review_stage?: string;
  decision?: string;
  comments?: string | null;
  findings?: unknown[];
  recommendations?: unknown[];
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface ApplicationTimelineRecord {
  id: string;
  application_id?: string;
  event_type?: string;
  from_status?: string | null;
  to_status?: string | null;
  from_stage?: string | null;
  to_stage?: string | null;
  actor_id?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown>;
  created_at?: string;
}

export interface ApplicationRecord {
  id: string;
  type: string;
  status: 'submitted' | 'in-review' | 'approved' | 'rejected' | string;
  submittedAt?: string;
  reference?: string;
  currentStage?: string;
  createdAt?: string;
  updatedAt?: string;

  application_number?: string;
  application_type?: string;
  current_stage?: string;
  submitted_at?: string | null;
  created_at?: string;
  updated_at?: string;
  title?: string;
  summary?: string | null;
  priority?: number;
  metadata?: Record<string, unknown>;
  document_count?: number;
  review_count?: number;
  last_event_at?: string | null;
}

export type NotificationCategory =
  | 'workflow' | 'agreement' | 'payment' | 'document'
  | 'gis' | 'system' | 'support' | 'pilot_update';

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  read: boolean;
  /** Optional category supplied by geo-api; defaults to 'system' in the UI. */
  category?: NotificationCategory | string;
}

/* ── Agreement Centre read model (LOI / NDA / MOU consolidated) ── */

export type AgreementType = 'LOI' | 'NDA' | 'MOU' | string;

export type SignatureStatus = 'not_required' | 'pending' | 'signed' | string;

export interface AgreementRecord {
  id: string;
  type: AgreementType;
  title?: string;
  reference?: string;
  status?: string;
  investorSignature?: SignatureStatus;
  authoritySignature?: SignatureStatus;
  effectiveDate?: string;
  updatedAt?: string;
  /** Server-authorised view/download URL when available. */
  documentUrl?: string;
  /** Available next action label supplied by geo-api, if any. */
  availableAction?: string;
}

/* ── Document Centre read model ── */

export type DocumentCategory =
  | 'investor' | 'kyc' | 'agreement' | 'invoice' | 'receipt'
  | 'project' | 'gis_request' | 'correspondence' | string;

export interface PortalDocument {
  id: string;
  name: string;
  category?: DocumentCategory;
  status?: string;
  uploadedAt?: string;
  uploadedBy?: string;
  verificationStatus?: string;
  /** Server-authorised view/download URL when available. */
  url?: string;
}



/* ── Investor Profile Centre account model ── */

export interface InvestorAccountProfile {
  id?: string;
  email?: string | null;
  status?: InvestorStatus;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  nationality?: string | null;
  id_type?: string | null;
  id_number?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  country?: string | null;
  investor_type?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface InvestorCompanyProfile {
  id?: string;
  name?: string | null;
  organization_name?: string | null;
  legal_name?: string | null;
  trading_name?: string | null;
  registration_number?: string | null;
  tax_id?: string | null;
  incorporation_country?: string | null;
  incorporation_date?: string | null;
  sector?: string | null;
  sub_sector?: string | null;
  website?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  headquarters_address?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface InvestorAccountEnvelope {
  investor: InvestorAccountProfile | null;
  company: InvestorCompanyProfile | null;
}

export interface InvestorProfileUpdatePayload {
  first_name?: string;
  last_name?: string;
  phone?: string;
  nationality?: string;
  id_type?: string;
  id_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  country?: string;
  investor_type?: string;
}

export interface InvestorCompanyUpdatePayload {
  legal_name?: string;
  trading_name?: string;
  registration_number?: string;
  tax_id?: string;
  incorporation_country?: string;
  incorporation_date?: string;
  sector?: string;
  sub_sector?: string;
  website?: string;
  contact_email?: string;
  contact_phone?: string;
  headquarters_address?: string;
}

const FALLBACK_PROFILE: InvestorProfileSummary = {
  status: 'pending',
  ndaSigned: false,
  mouSigned: false,
  loiSubmitted: false,
  kycComplete: false,
};

const FALLBACK_KYC_PROFILE: InvestorKycProfile = {
  kycStatus: 'not_started',
  completenessScore: 0,
  projectInterests: [],
  mineralInterests: [],
  documentsUploaded: 0,
};


export const investorService = {
  async getInvestorAccount(): Promise<InvestorAccountEnvelope> {
    const res = await geoApi.get<InvestorAccountEnvelope>('/investors/me');

    if (res.error) {
      throw new Error(String(res.error));
    }

    return {
      investor: res.data?.investor ?? null,
      company: res.data?.company ?? null,
    };
  },

  updateInvestorProfile(payload: InvestorProfileUpdatePayload) {
    return geoApi.patch<{
      ok?: boolean;
      investor?: InvestorAccountProfile;
    }>('/investors/me/profile', payload);
  },

  updateInvestorCompany(payload: InvestorCompanyUpdatePayload) {
    return geoApi.patch<{
      ok?: boolean;
      company?: InvestorCompanyProfile;
    }>('/investors/me/company', payload);
  },

  async getProfile(): Promise<InvestorProfileSummary> {
    type InvestorEnvelope = {
      investor?: {
        status?: InvestorStatus;
        first_name?: string | null;
        last_name?: string | null;
      } | null;
      company?: {
        name?: string | null;
        organization_name?: string | null;
      } | null;
    };

    type KycEnvelope = {
      kyc?: {
        requiredComplete?: boolean;
        completenessScore?: number;
      } | null;
      workflow?: {
        stages?: Array<{
          key?: string;
          status?: string;
        }>;
      } | null;
    };

    type WorkflowEnvelope = {
      workflow?: {
        stages?: Array<{
          key?: string;
          status?: string;
        }>;
      } | null;
    };

    const [profileRes, kycRes, workflowRes] = await Promise.all([
      geoApi.get<InvestorEnvelope>('/investors/me'),
      geoApi.get<KycEnvelope>('/investors/me/kyc'),
      geoApi.get<WorkflowEnvelope>('/investors/me/workflow'),
    ]);

    if (profileRes.error || !profileRes.data?.investor) {
      throw readFailure(profileRes, 'Investor profile');
    }

    const rawInvestor = profileRes.data.investor;
    const stages =
      workflowRes.data?.workflow?.stages ??
      kycRes.data?.workflow?.stages ??
      [];

    const completed = (key: string): boolean =>
      stages.some(
        (stage) =>
          String(stage.key || '').toUpperCase() === key &&
          ['completed', 'approved', 'signed'].includes(
            String(stage.status || '').toLowerCase()
          )
      );

    const fullName =
      [rawInvestor.first_name, rawInvestor.last_name]
        .filter(Boolean)
        .join(' ') || undefined;

    return {
      status: rawInvestor.status ?? 'registered_investor',
      ndaSigned: completed('NDA'),
      mouSigned: completed('MOU'),
      loiSubmitted: completed('LOI'),
      kycComplete: Boolean(kycRes.data?.kyc?.requiredComplete),
      kycCompletenessScore:
        typeof kycRes.data?.kyc?.completenessScore === 'number'
          ? kycRes.data.kyc.completenessScore
          : 0,
      // Unknown is not "not signed" / "0%": flag reads that failed instead of defaulting.
      stagesUnavailable: !!workflowRes.error && !!kycRes.error,
      kycUnavailable: !!kycRes.error,
      organization:
        profileRes.data.company?.name ??
        profileRes.data.company?.organization_name ??
        undefined,
      fullName,
    };
  },
  submitKyc(payload: KycPayload) {
    return geoApi.patch<{ ok: true }>('/investors/me/kyc', payload);
  },
  async requestDocument(payload: DocumentRequestPayload) {
    const created = await geoApi.post<ApplicationRecord>('/investors/applications', {
      applicationType: 'document_request',
      title: `Document request: ${payload.documentType}`,
      summary: payload.reason,
      metadata: {
        documentType: payload.documentType,
        ...(payload.projectId ? { projectId: payload.projectId } : {}),
      },
    });

    if (created.error || !created.data?.id) {
      return created;
    }

    return geoApi.post<ApplicationRecord>(
      `/investors/applications/${created.data.id}/submit`,
      {
        notes: 'Document request submitted by investor.',
        metadata: { source: 'investor_portal' },
      }
    );
  },
  async applyForPermit(payload: PermitApplicationPayload) {
    const created = await geoApi.post<ApplicationRecord>('/investors/applications', {
      applicationType: 'permit',
      title: `Permit application: ${payload.permitType}`,
      summary: payload.notes || null,
      metadata: {
        permitType: payload.permitType,
        region: payload.region,
        ...(payload.projectId ? { projectId: payload.projectId } : {}),
      },
    });

    if (created.error || !created.data?.id) {
      return created;
    }

    return geoApi.post<ApplicationRecord>(
      `/investors/applications/${created.data.id}/submit`,
      {
        notes: 'Permit application submitted by investor.',
        metadata: { source: 'investor_portal' },
      }
    );
  },
  async getApplication(applicationId: string) {
    return geoApi.get<ApplicationRecord>(
      `/investors/applications/${applicationId}`
    );
  },

  async listApplicationDocuments(applicationId: string) {
    return geoApi.get<{ items?: ApplicationDocumentRecord[] }>(
      `/investors/applications/${applicationId}/documents`
    );
  },

  async uploadApplicationDocument(
    applicationId: string,
    file: File,
    options: {
      docType: string;
      title?: string;
      metadata?: Record<string, unknown>;
    }
  ) {
    const form = new FormData();
    form.append('file', file);
    form.append('docType', options.docType);

    if (options.title?.trim()) {
      form.append('title', options.title.trim());
    }

    if (options.metadata) {
      form.append('metadata', JSON.stringify(options.metadata));
    }

    return geoApi.postForm<ApplicationDocumentRecord>(
      `/investors/applications/${applicationId}/documents`,
      form
    );
  },

  async listApplicationReviews(applicationId: string) {
    return geoApi.get<{ items?: ApplicationReviewRecord[] }>(
      `/investors/applications/${applicationId}/reviews`
    );
  },

  async getApplicationTimeline(applicationId: string) {
    return geoApi.get<{ items?: ApplicationTimelineRecord[] }>(
      `/investors/applications/${applicationId}/timeline`
    );
  },

  async listApplications() {
    type RawApplicationRecord = ApplicationRecord & {
      application_number?: string;
      application_type?: string;
      submitted_at?: string | null;
      created_at?: string;
      updated_at?: string;
      current_stage?: string;
    };

    const res = await geoApi.get<{
      items?: RawApplicationRecord[];
      total?: number;
    }>('/investors/applications');

    const items = Array.isArray(res.data?.items) ? res.data.items : [];

    return {
      data: items.map((raw) => ({
        ...raw,
        reference: raw.reference ?? raw.application_number ?? raw.id,
        type: raw.type ?? raw.application_type,
        submittedAt: raw.submittedAt ?? raw.submitted_at ?? undefined,
        currentStage: raw.currentStage ?? raw.current_stage ?? undefined,
        createdAt: raw.createdAt ?? raw.created_at ?? undefined,
        updatedAt: raw.updatedAt ?? raw.updated_at ?? undefined,
      })),
      error: res.error,
      status: res.status,
    };
  },
  async requestRestrictedData(payload: RestrictedDataPayload) {
    const created = await geoApi.post<ApplicationRecord>('/investors/applications', {
      applicationType: 'restricted_data',
      title: `Restricted data request: ${payload.datasetId}`,
      summary: payload.justification,
      metadata: {
        datasetId: payload.datasetId,
        justification: payload.justification,
        ndaAcknowledged: payload.ndaAcknowledged,
      },
    });

    if (created.error || !created.data?.id) {
      return created;
    }

    return geoApi.post<ApplicationRecord>(
      `/investors/applications/${created.data.id}/submit`,
      {
        notes: 'Restricted data request submitted by investor.',
        metadata: {
          source: 'investor_portal',
          ndaAcknowledged: payload.ndaAcknowledged,
        },
      }
    );
  },
  async requestAgreement(_payload: AgreementRequestPayload) {
    return {
      data: null,
      error: 'Agreement requests are managed through the approved GIISDEC workflow and are not yet available from this portal action.',
      status: 503,
    };
  },
  submitSupportTicket(payload: SupportTicketPayload) {
    return geoApi.post<{ id: string }>('/support', payload);
  },
  async listNotifications() {
    type RawNotificationItem = {
      id: string;
      subject?: string | null;
      title?: string | null;
      body?: string | null;
      created_at?: string | null;
      createdAt?: string | null;
      read_at?: string | null;
      read?: boolean;
      category?: string | null;
    };

    type NotificationEnvelope = {
      data?: RawNotificationItem[];
    };

    const res = await geoApi.get<NotificationEnvelope>('/notifications/me');
    const rows = Array.isArray(res.data?.data) ? res.data.data : [];

    return {
      data: rows.map((raw): NotificationItem => ({
        id: raw.id,
        title: raw.title ?? raw.subject ?? 'Notification',
        body: raw.body ?? '',
        createdAt: raw.createdAt ?? raw.created_at ?? '',
        read:
          typeof raw.read === 'boolean'
            ? raw.read
            : Boolean(raw.read_at),
        category: raw.category ?? 'system',
      })),
      error: res.error,
      status: res.status,
    };
  },
  async listReviewTimeline(applicationId?: string) {
    if (!applicationId) {
      return {
        data: [] as ReviewTimelineEntry[],
        error: null,
        status: 404,
      };
    }

    const res = await geoApi.get<{ items?: ApplicationTimelineRecord[] }>(
      `/investors/applications/${encodeURIComponent(applicationId)}/timeline`
    );

    const items = Array.isArray(res.data?.items)
      ? res.data.items.map((item) => {
          const rawState = String(
            item.to_status ??
            item.to_stage ??
            item.event_type ??
            ''
          ).toLowerCase();

          const state: ReviewTimelineEntry['state'] =
            /reject|declin|fail/.test(rawState)
              ? 'rejected'
              : /approv|complete|accepted/.test(rawState)
                ? 'approved'
                : /review|progress|process|submit|pending/.test(rawState)
                  ? 'in-progress'
                  : 'pending';

          return {
            id: item.id,
            step:
              item.to_stage ??
              item.to_status ??
              item.event_type ??
              'Application update',
            state,
            actor: item.actor_id ?? undefined,
            at: item.created_at,
            note: item.notes ?? undefined,
          };
        })
      : [];

    return {
      data: items,
      error: res.error,
      status: res.status,
    };
  },

  /* G1C1: the geo-api contract has no investor-scoped document-access-history read. The
   * result is therefore NOT PROVIDED (data: null) — never an authoritative empty history. */
  async listDocumentAccessHistory(): Promise<{ data: DocumentAccessEntry[] | null; error: string | null; status: number }> {
    return { data: null, error: null, status: 0 };
  },
  /* ── Enhanced KYC + workflow governance (safe-default reads) ── */

  async getKycProfile(): Promise<InvestorKycProfile> {
    type KycField = {
      key?: string;
      value?: unknown;
      present?: boolean;
    };

    type KycEnvelope = {
      kyc?: {
        status?: string;
        completenessScore?: number;
        fields?: KycField[];
        submittedAt?: string;
      } | null;
      documents?: {
        count?: number;
      } | null;
    };

    const res = await geoApi.get<KycEnvelope>('/investors/me/kyc');
    if (res.error) throw readFailure(res, 'KYC profile'); // unavailable, not "not started"
    const raw = res.data?.kyc;

    if (!raw) return FALLBACK_KYC_PROFILE; // authoritative response with no KYC record

    const fieldValue = (key: string): unknown =>
      raw.fields?.find((field) => field.key === key)?.value;

    const stringValue = (key: string): string | undefined => {
      const value = fieldValue(key);
      return typeof value === 'string' && value.trim()
        ? value.trim()
        : undefined;
    };

    const arrayValue = (key: string): string[] | undefined => {
      const value = fieldValue(key);

      if (Array.isArray(value)) {
        const items = value
          .filter((item): item is string => typeof item === 'string')
          .map((item) => item.trim())
          .filter(Boolean);

        return items.length > 0 ? items : undefined;
      }

      if (typeof value === 'string' && value.trim()) {
        const items = value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

        return items.length > 0 ? items : undefined;
      }

      return undefined;
    };

    return {
      kycStatus: (raw.status ?? 'not_started') as KycStatus,
      completenessScore: Number(raw.completenessScore ?? 0),
      email: stringValue('email'),
      phone: stringValue('phone'),
      organizationName: stringValue('organization_name'),
      organizationRegistration: stringValue('registration_number'),
      organizationAddress: stringValue('organization_address'),
      countryOfOperation: stringValue('country_of_operation'),
      businessCategory: stringValue('business_category'),
      representativeName: stringValue('representative_name'),
      representativeRole: stringValue('representative_role'),
      representativeEmail: stringValue('representative_email'),
      idType: stringValue('id_type'),
      idNumber: stringValue('id_number'),
      taxId: stringValue('tax_id'),
      investorClassification: stringValue('investor_classification'),
      projectInterests: arrayValue('project_interest_areas'),
      mineralInterests: arrayValue('concession_interests'),
      documentsUploaded: Number(res.data?.documents?.count ?? 0),
      updatedAt: raw.submittedAt,
    };
  },
  submitEnhancedKyc(payload: EnhancedKycPayload) {
    return geoApi.patch<{ ok: true }>('/investors/me/kyc', payload);
  },
  async getWorkflowStages(): Promise<WorkflowStage[]> {
    type WorkflowEnvelope = {
      workflow?: {
        stages?: WorkflowStage[];
      } | null;
    };

    const res = await geoApi.get<WorkflowEnvelope>('/investors/me/workflow');
    if (res.error) throw readFailure(res, 'Investor workflow');
    const stages = res.data?.workflow?.stages;
    return Array.isArray(stages) ? stages : [];
  },
  /* G1C1: no document-lifecycle read exists in the geo-api contract — NOT PROVIDED (null),
   * never an authoritative "no workflow documents". */
  async listDocumentLifecycle(): Promise<DocumentLifecycleEntry[] | null> {
    return null;
  },

  /* ── Agreement Centre (consolidated LOI / NDA / MOU reads) ──
   * Uses the authenticated /agreements/me read contract. An authoritative empty list is
   * returned as []; a failed read throws InvestorReadError (VUI1-FIX1). */
  async listAgreements(): Promise<AgreementRecord[]> {
    const res = await geoApi.get<AgreementRecord[]>('/agreements/me');
    if (res.error) throw readFailure(res, 'Agreements'); // a failed read is not "no agreements"
    return Array.isArray(res.data) ? res.data : [];
  },

  /* ── Document Centre (consolidated document reads) ── */
  async listDocuments(): Promise<PortalDocument[]> {
    const res = await geoApi.get<PortalDocument[]>('/investors/documents');
    if (res.error) throw readFailure(res, 'Documents'); // a failed read is not "0 documents"
    return Array.isArray(res.data) ? res.data : [];
  },

  /* ── Notification actions (server remains authoritative) ── */
  markNotificationRead(id: string) {
    return geoApi.post<{ data?: unknown }>(
      `/notifications/${encodeURIComponent(id)}/read`,
      {}
    );
  },
  markAllNotificationsRead() {
    return geoApi.post<{ data?: unknown }>(
      '/notifications/me/read-all',
      {}
    );
  },


  /** Investor access rules helper — kept on frontend for UI gating only.
   * Server still enforces these via geo-api. */
  canDownload(profile: InvestorProfileSummary, requires: 'NDA' | 'MOU' | 'NONE' = 'NONE'): boolean {
    const verifiedStatuses: InvestorStatus[] = [
      'verified_investor', 'shortlisted_investor', 'approved_investor', 'strategic_partner',
      // legacy
      'verified',
    ];
    if (!verifiedStatuses.includes(profile.status)) return false;
    if (profile.status === 'suspended_investor' || profile.status === 'restricted_access_investor') return false;
    if (requires === 'NDA' && !profile.ndaSigned) return false;
    if (requires === 'MOU' && !profile.mouSigned) return false;
    return true;
  },
};

/* ───────────────── KYC + workflow governance helpers ─────────────────
 * Pure, side-effect-free helpers used by the UI. They never throw and always
 * tolerate partial / undefined data so dashboards render safe empty states.
 */

/** KYC fields that count toward the onboarding completeness score. */
const KYC_SCORED_FIELDS: (keyof InvestorKycProfile)[] = [
  'email', 'phone', 'organizationName', 'organizationRegistration',
  'organizationAddress', 'countryOfOperation', 'businessCategory',
  'representativeName', 'idType', 'idNumber',
];

/** Compute a 0..100 onboarding completeness score from a (possibly partial)
 * KYC profile. Prefers a server-provided score when present and > 0. */
export function computeKycCompleteness(p: InvestorKycProfile | null): number {
  if (!p) return 0;
  if (typeof p.completenessScore === 'number' && p.completenessScore > 0) {
    return Math.max(0, Math.min(100, Math.round(p.completenessScore)));
  }
  let filled = 0;
  for (const k of KYC_SCORED_FIELDS) {
    const v = p[k];
    if (typeof v === 'string' && v.trim().length > 0) filled += 1;
  }
  if ((p.projectInterests?.length ?? 0) > 0) filled += 0; // optional, not scored
  return Math.round((filled / KYC_SCORED_FIELDS.length) * 100);
}

export interface KycStatusMeta {
  label: string;
  tone: 'neutral' | 'pending' | 'review' | 'approved' | 'rejected';
}

export function kycStatusMeta(s: KycStatus | undefined): KycStatusMeta {
  switch (s) {
    case 'approved': return { label: 'Approved', tone: 'approved' };
    case 'rejected': return { label: 'Rejected', tone: 'rejected' };
    case 'under_review': return { label: 'Under review', tone: 'review' };
    case 'submitted': return { label: 'Submitted', tone: 'review' };
    case 'incomplete': return { label: 'Incomplete', tone: 'pending' };
    case 'pending': return { label: 'Pending', tone: 'pending' };
    default: return { label: 'Not started', tone: 'neutral' };
  }
}

/** Official GIISDEC sequential workflow order: LOI → NDA → Payment → MOU. */
export const WORKFLOW_ORDER: WorkflowStageKey[] = ['loi', 'nda', 'payment', 'mou'];

export const WORKFLOW_LABELS: Record<WorkflowStageKey, string> = {
  loi: 'Letter of Intent (LOI)',
  nda: 'Non-Disclosure (NDA)',
  payment: 'Payment Validation',
  mou: 'Memorandum (MOU)',
};

/** Derive a consistent, fully-ordered 4-stage workflow with future-locking.
 * Server-provided stages take precedence; missing stages are inferred from the
 * investor profile summary and gated so a stage is "locked" until every prior
 * stage is approved. Always returns exactly 4 ordered stages. */
export function deriveWorkflowStages(
  serverStages: WorkflowStage[] | null | undefined,
  profile: InvestorProfileSummary | null,
): WorkflowStage[] {
  const byKey = new Map<WorkflowStageKey, WorkflowStage>();
  (serverStages ?? []).forEach((s) => {
    if (s && WORKFLOW_ORDER.includes(s.key)) byKey.set(s.key, s);
  });

  // Inferred baseline status from the profile summary when server is silent.
  const inferred: Record<WorkflowStageKey, WorkflowStageStatus> = {
    loi: profile?.loiSubmitted ? 'submitted' : 'available',
    nda: profile?.ndaSigned ? 'approved' : 'locked',
    payment: 'locked',
    mou: profile?.mouSigned ? 'approved' : 'locked',
  };

  let priorApproved = true;
  return WORKFLOW_ORDER.map((key) => {
    const server = byKey.get(key);
    let status: WorkflowStageStatus = server?.status ?? inferred[key];
    // Future-locking: a stage cannot be open if any prior stage is unfinished.
    if (!priorApproved && status !== 'approved' && status !== 'rejected') {
      status = 'locked';
    }
    const stage: WorkflowStage = {
      key,
      status,
      updatedAt: server?.updatedAt,
      note: server?.note,
    };
    priorApproved = priorApproved && status === 'approved';
    return stage;
  });
}

/** Identify the single next action the investor must take. */
export function nextWorkflowAction(stages: WorkflowStage[]): { key: WorkflowStageKey; label: string } | null {
  for (const s of stages) {
    if (s.status === 'available' || s.status === 'in_progress' || s.status === 'rejected') {
      return { key: s.key, label: WORKFLOW_LABELS[s.key] };
    }
  }
  return null;
}
